import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `account` domain — the two tabs of `/account`.
 *
 * Both are `partially-wired`, each for its own reason:
 *
 *  - profile: the display-name form PATCHes `/admins/:id` with `{ name }`, but
 *    `PatchBody` carries no `name` key and `admin.admins` has no such column, so
 *    Elysia's default `normalize` strips it and `updateAdmin` writes email /
 *    roleId / level back unchanged. The mutation resolves, the page toasts
 *    "saved", and the store is updated optimistically — nothing persisted.
 *    `displayName` at login is just `adminRow.username`, so it reverts.
 *  - security: only Change password works. "Verify & enable" posts to
 *    `/auth/mfa/verify`, a path svc-auth does not define (the route is
 *    `/enroll/verify` under the `/auth/mfa` prefix) → 404 on every attempt.
 *    Because `mfaEnabled` is local React state flipped only by that handler's
 *    success, the disable branch is unreachable — and its body (`currentPassword`,
 *    no `code`) would fail `DisableBody` validation anyway.
 */
export const accountGuide: DomainGuide = {
  domain: 'account',
  pages: [
    {
      id: 'account.profile',
      route: '/account?tab=profile',
      permission: 'admin:update',
      status: 'partially-wired',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'email', kind: 'text' },
        { key: 'role', kind: 'text' },
        { key: 'level', kind: 'count' },
        { key: 'lastLogin', kind: 'date' },
        { key: 'displayName', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'account.security',
      route: '/account?tab=security',
      // `/auth/*` is `authRequired: false` at the gateway; svc-auth binds the
      // subject to the caller's own token, so no RBAC code gates this screen.
      permission: 'none (any signed-in staff, own account only)',
      status: 'partially-wired',
      fields: [
        { key: 'currentPassword', kind: 'text' },
        { key: 'newPassword', kind: 'text' },
        { key: 'mfaState', kind: 'status' },
        { key: 'otpauthUrl', kind: 'text' },
        { key: 'mfaSecret', kind: 'text' },
        { key: 'mfaCode', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 1,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
